import React from "react";
import { Button } from "@/components/Button";
import Sticky from "@/components/Sticky";
import { shopifyCartApi } from "@/services/shopifyCart";
import { useCartUiActions } from "../cart";
import { usePdpProduct, usePdpSellingPlan, usePdpVariant } from "./PdpContext";
import { productSelect, variantSelect } from "@/store";
import { useMediaQuery } from "@mui/material";
import theme from "@/theme";
import { getFinalAtcCtaPrice } from "@/utils/atcCTAPrice";

const StickyAddToCartButton = ({
  product,
  variant,
  atcCTAPrice,
  onetimeDisplayPrice,
  ctaButtonLabel,
}: {
  product: any;
  variant: any;
  atcCTAPrice: any;
  onetimeDisplayPrice: any;
  ctaButtonLabel: string;
}) => {
  const shopifyCartAdder = shopifyCartApi.useTrackedAdd();
  const cartActions = useCartUiActions();
  const sellingPlan = usePdpSellingPlan();
  return (
    <Button
      className="group h-full w-full grow bg-green-500 text-base text-primary not-disabled:hover:bg-green-900 not-disabled:hover:text-white lg:w-60 lg:grow-0"
      style={{ borderRadius: 0 }}
      onClick={() => {
        const { variantToAdd, quantity } =
          variantSelect.resolveVariantAndQuantityForCartAdd(
            variant,
            product.variants
          );
        const lineItem = variantSelect.cartAddLineItem(variantToAdd, {
          subscription: sellingPlan,
          quantity,
          product,
        });

        const body = { items: [lineItem], sourceModule: "sticky_atc" };
        shopifyCartAdder.mutate(body, {
          onSuccess: () => cartActions.openDrawer(),
        });
      }}
    >
      <div className="flex w-full items-center justify-between lg:justify-center">
        <span>{ctaButtonLabel}</span>
        {
          <div className="flex items-center gap-2 group-hover:text-white lg:hidden">
            {product?.type != "Bundle" &&
              (variant.compareAtPrice &&
              variant.compareAtPrice.asCents() > variant.price.asCents() ? (
                <div className="text-sm font-normal tracking-normal text-black group-hover:text-white">
                  <span className="line-through">
                    {variant.compareAtPrice.format()}
                  </span>
                </div>
              ) : sellingPlan &&
                atcCTAPrice.currentPrice != onetimeDisplayPrice.format() ? (
                <div className="text-sm font-normal tracking-normal text-black group-hover:text-white">
                  <span className="line-through">
                    {onetimeDisplayPrice.format()}
                  </span>
                </div>
              ) : null)}
            {product?.type === "Bundle" && variant.compareAtPrice && (
              <div className="text-sm font-normal tracking-normal text-black group-hover:text-white">
                <span className="line-through">
                  {variant.compareAtPrice.format()}
                </span>
              </div>
            )}
            <span className="text-xl tracking-normal text-black group-hover:text-white">
              {atcCTAPrice.display}
            </span>
          </div>
        }
      </div>
    </Button>
  );
};

export const StickyAddToCart = () => {
  const product = usePdpProduct();
  const variant = usePdpVariant();
  const sellingPlan = usePdpSellingPlan();
  const isLargeScreen = useMediaQuery(
    theme.breakpoints.up(theme.breakpoints.values.lg)
  );
  const subscription = variantSelect.orderableSubscription(variant);
  const onetimeDisplayPrice = variantSelect.onetimePrice(variant, product);
  const subscriptionDisplayPrice = variantSelect.lowestPrice(variant, product);

  // Move hooks before early return
  const isTrialProduct = product?.tags?.includes("trial-product");
  const ctaButtonLabel = React.useMemo(() => {
    if (!variant.available) {
      return "Out of Stock";
    }

    if (isTrialProduct) {
      return "Start Your Trial";
    }

    return "Add To Cart";
  }, [variant.available, isTrialProduct]);

  // Early return after all hooks
  if (!variant.available) {
    return null;
  }

  // Use the new promo price utility
  const atcCTAPrice = getFinalAtcCtaPrice({
    productCollections: product.collections,
    basePriceCents: sellingPlan
      ? subscriptionDisplayPrice.asCents()
      : onetimeDisplayPrice.asCents(),
    sellingPlan,
    variant,
    product,
    currency: "$",
  });

  return (
    <Sticky
      displayThreshold={isLargeScreen ? 700 : 1000}
      anchor="bottom"
      timeout={100}
    >
      <div className="flex h-16 items-center bg-white lg:h-20 lg:pl-11">
        <div className="hidden grow items-center space-x-4 pr-12 lg:flex">
          <div className="flex h-12 items-center border-0 border-r border-solid border-primary-200 pr-4">
            <div className="text-lg font-semibold xl:text-2xl">
              {productSelect.name(product)}
            </div>
          </div>
          <div className="whitespace-nowrap border-0 border-r border-solid border-primary-200 pr-4">
            {productSelect.scent(product)}
          </div>
          <div className="whitespace-nowrap border-0 border-r border-solid border-primary-200 pr-4">
            {variantSelect.sizeFormat(variant)}
          </div>
          <div className="whitespace-nowrap">
            {subscription && sellingPlan ? "Subscription" : "One-time"}
          </div>
        </div>
        <div className="flex h-full grow items-center lg:grow-0">
          {
            <div className="ml-auto hidden items-center gap-2 lg:flex lg:pr-5">
              {product?.type != "Bundle" &&
                (variant.compareAtPrice &&
                variant.compareAtPrice.asCents() > variant.price.asCents() ? (
                  <div className="text-sm font-normal tracking-normal text-black">
                    <span className="line-through">
                      {variant.compareAtPrice.format()}
                    </span>
                  </div>
                ) : sellingPlan &&
                  atcCTAPrice.currentPrice != onetimeDisplayPrice.format() ? (
                  <div className="text-sm font-normal tracking-normal text-black">
                    <span className="line-through">
                      {onetimeDisplayPrice.format()}
                    </span>
                  </div>
                ) : null)}
              {product?.type === "Bundle" && variant.compareAtPrice && (
                <div className="text-sm font-normal tracking-normal text-black">
                  <span className="line-through">
                    {variant.compareAtPrice.format()}
                  </span>
                </div>
              )}
              <span className="text-xl tracking-normal text-black">
                {atcCTAPrice.display}
              </span>
            </div>
          }

          <StickyAddToCartButton
            product={product}
            variant={variant}
            atcCTAPrice={atcCTAPrice}
            onetimeDisplayPrice={onetimeDisplayPrice}
            ctaButtonLabel={ctaButtonLabel}
          />
        </div>
      </div>
    </Sticky>
  );
};
